import type {
  ChartDataPoint,
  ChartDataTableProps,
  ChartPointValue,
  ItemPayload,
  LineDefinition,
} from './types';
import { isSyntheticPoint } from './utils';

/**
 * The plotted points exist only as SVG shapes, which carry neither a name nor a
 * role. This table is the accessible equivalent of the drawn line: it repeats
 * the very same points, formatted the very same way as in the tooltip, as rows
 * and cells that assistive technology can read and navigate.
 *
 * It is visually hidden because sighted users already have the graphic.
 */
export function ChartDataTable<
  DataPoint extends ChartDataPoint = ChartDataPoint,
>({
  caption,
  data,
  labelFormatter,
  lines,
  valueFormatter,
  xAxisLabel,
  xKey,
}: ChartDataTableProps<DataPoint>) {
  const rows = data.filter((point) => !isSyntheticPoint(point));

  if (rows.length === 0 || lines.length === 0) return null;

  return (
    // Keep `sr-only` on a non-table wrapper. Browsers let the table layout
    // override the utility's 1px dimensions, causing its rows to create a
    // large invisible scroll area below the page footer.
    <div className='eye:sr-only'>
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope='col'>{xAxisLabel}</th>
            {lines.map((line) => (
              <th key={line.dataKey} scope='col'>
                {line.label ?? line.dataKey}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((point, rowIndex) => (
            <tr key={`${String(point[xKey])}-${rowIndex}`}>
              <th scope='row'>
                {formatPointLabel(point[xKey], labelFormatter)}
              </th>
              {lines.map((line) => (
                <td key={line.dataKey}>
                  {formatPointValue(point[line.dataKey], line, valueFormatter)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatPointLabel(
  value: ChartPointValue,
  labelFormatter: ChartDataTableProps['labelFormatter'],
) {
  if (labelFormatter) return labelFormatter(value);

  return value instanceof Date ? value.toLocaleString() : String(value ?? '');
}

function formatPointValue<DataPoint extends ChartDataPoint>(
  value: ChartPointValue,
  line: LineDefinition<DataPoint>,
  valueFormatter: ChartDataTableProps['valueFormatter'],
) {
  // A gap in a series stays a gap: an empty cell is read out as such.
  if (value == null) return null;

  if (!valueFormatter) return String(value);

  // The formatter reads the unit off the tooltip payload, so the table has to
  // hand it the same shape to end up with the same text.
  const item = {
    dataKey: line.dataKey,
    graphicalItemId: line.dataKey,
    name: line.label,
    unit: line.unit,
    value,
  } as ItemPayload;

  return valueFormatter(value, item);
}
